import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Label, Modal, TextInput } from "flowbite-react";
import { UserAuth } from "../../lib/authContext";
import { clienthubApiClient } from "../../lib/apiClient";

interface UploadPostModalProps {
  open: boolean;
  onClose: () => void;
  fetchPosts: () => Promise<void>;
}

const CreatePost: React.FC<UploadPostModalProps> = ({
  open,
  onClose,
  fetchPosts,
}) => {
  const { event_id } = useParams();
  const { userData } = UserAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(selectedFiles);
    setPreviewUrls(selectedFiles.map((file) => URL.createObjectURL(file)));
    setStep(2);
  };

  const handleUpload = async () => {
    if (!event_id || !userData?.user_id || !files.length) return;

    const formData = new FormData();
    formData.append("post_title", title);
    formData.append("event_id", event_id);
    formData.append("user_id", userData.user_id);
    files.forEach((file) => formData.append("media", file));

    try {
      setUploading(true);
      const res = await clienthubApiClient.post("/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      //   console.log(res);
      fetchPosts();
      setStep(1);
      setFiles([]);
      setPreviewUrls([]);
      setTitle("");
      onClose();
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>{step === 1 ? "Upload Media" : "Add Title"}</Modal.Header>
      <Modal.Body className="bg-white">
        {step === 1 && (
          <div className="space-y-4">
            <Label htmlFor="media" value="Select photos or videos" />
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              className="block w-full text-black"
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {files.map((file, idx) =>
                file.type.startsWith("video/") ? (
                  <video key={idx} controls className="w-full rounded-xl">
                    <source src={previewUrls[idx]} type={file.type} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    key={idx}
                    src={previewUrls[idx]}
                    className="w-full rounded-xl"
                    alt="Preview"
                  />
                )
              )}
            </div>
            <div>
              <Label htmlFor="post_title" value="Post Title" />
              <TextInput
                id="post_title"
                placeholder="Enter a title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-white">
        {step === 2 && (
          <div className="flex justify-between w-full">
            <Button color="gray" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              onClick={handleUpload}
              isProcessing={uploading}
              className="text-black border-0 bg-gradient-to-b from-[#fce96a] to-[#e3a008] rounded-md focus:outline-none hover:from-yellow-500 hover:to-yellow-400 "
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreatePost;
